// Change requests of customers from the auftrag portal (AB4): the view "Änderungsanträge von Kunden" and the same cards
// on the company page. Per field the value now and the requested one, "Übernehmen" or "Ablehnen", an optional comment
// for the customer, then "Entscheiden": the accepted fields are saved and auftrag hears the result.
import type { ChangeDecision, ChangeRequest } from "../api";
import { S, write } from "../state";
import { $, emptyState, esc, fmtDT, toast } from "../util";

/** Choices made but not sent yet, kept over the redraws of the 60-second refresh. */
const choices = new Map<string, { picks: Record<string, ChangeDecision>; comment: string }>();
const choice = (id: string) => choices.get(id) ?? choices.set(id, { picks: {}, comment: "" }).get(id)!;

function title(r: ChangeRequest): string {
  if (r.target === "firma") return `Firma „${esc(r.company.name)}“ ändern`;
  if (r.creates) return r.target === "person" ? "Neue Person" : "Neue Adresse";
  if (!r.record) return r.target === "person" ? "Person (nicht mehr vorhanden)" : "Adresse (inzwischen gelöscht)";
  return `${r.target === "person" ? "Person" : "Adresse"} „${esc(r.record.label)}“ ändern`;
}

const value = (v: string | null) => (v === null ? `<span class="muted">leer</span>` : esc(v));

/** One request; `withCompany` on the list of all requests. */
export function changeRequestCard(r: ChangeRequest, withCompany: boolean): string {
  const c = choice(r.id);
  const ro = S.readOnly;
  // Only refusing is left when the person or address is gone.
  const gone = !r.creates && !r.record;
  const who = [r.submittedBy.name, r.submittedBy.email].filter(Boolean).join(" · ");
  const rows = r.fields
    .map((f) => {
      const pick = c.picks[f.field];
      const changedSince = !r.creates && f.atSubmission !== f.current ? `<div class="muted small">beim Antrag: ${value(f.atSubmission)}</div>` : "";
      const buttons = ro
        ? ""
        : `<td class="cr-b">${gone ? "" : `<button type="button" class="btn${pick === "angenommen" ? " on" : ""}" aria-pressed="${pick === "angenommen"}" data-cr-pick="angenommen" data-field="${esc(f.field)}">Übernehmen</button>`}<button type="button" class="btn${pick === "abgelehnt" ? " on" : ""}" aria-pressed="${pick === "abgelehnt"}" data-cr-pick="abgelehnt" data-field="${esc(f.field)}">Ablehnen</button></td>`;
      return `<tr class="${pick ?? ""}"><th>${esc(f.label)}</th><td>${r.creates ? "" : `${value(f.current)}${changedSince}`}</td><td><b>${value(f.requested)}</b></td>${buttons}</tr>`;
    })
    .join("");
  return `<article class="cr" data-cr="${esc(r.id)}">
    <div class="cr-h"><b>${title(r)}</b>${withCompany ? ` <a href="#firma-${esc(r.company.id)}">${esc(r.company.name)}</a>` : ""}</div>
    <div class="muted small">Von ${esc(who)} · ${fmtDT(r.submittedAt)}</div>
    ${r.comment ? `<p class="cr-c">${esc(r.comment)}</p>` : ""}
    ${gone ? `<p class="note warn">Den Datensatz gibt es nicht mehr; der Antrag lässt sich nur ablehnen.</p>` : ""}
    <table class="cr-t"><thead><tr><th>Feld</th><th>${r.creates ? "" : "Jetzt"}</th><th>Gewünscht</th>${ro ? "" : "<th></th>"}</tr></thead><tbody>${rows}</tbody></table>
    ${
      ro
        ? ""
        : `<div class="cr-f">${gone ? "" : `<button type="button" class="btn" data-cr-all>Alle übernehmen</button>`}
      <textarea data-cr-comment rows="2" placeholder="Kommentar an den Kunden (optional)">${esc(c.comment)}</textarea>
      <button type="button" class="btn primary" data-cr-go>Entscheiden</button></div>`
    }</article>`;
}

export function renderAntraege(): void {
  const el = $("#v-antraege");
  const items = S.changeRequests;
  const head = `<div class="brief-head"><span class="eyebrow">Adressbuch</span><h1>Änderungsanträge von Kunden</h1>
    <p>Aus dem auftrag-Portal: je Feld übernehmen oder ablehnen. auftrag erfährt das Ergebnis.</p></div>`;
  el.innerHTML =
    head +
    (!items
      ? emptyState("Lade Anträge …", "")
      : items.length
        ? `<div class="crs">${items.map((r) => changeRequestCard(r, true)).join("")}</div>`
        : emptyState("Keine offenen Anträge", "Neue Anträge aus dem auftrag-Portal erscheinen hier und auf der Firmenseite."));
}

const find = (id: string) => (S.changeRequests ?? []).find((r) => r.id === id) ?? S.company?.changeRequests.find((r) => r.id === id);

/** Redraws one card in place (no reload while choosing). */
function repaint(card: HTMLElement, r: ChangeRequest): void {
  card.outerHTML = changeRequestCard(r, !!card.closest("#v-antraege"));
}

export function bindChangeRequests(): void {
  document.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-cr] [data-cr-pick], [data-cr] [data-cr-all], [data-cr] [data-cr-go]");
    if (!t || S.readOnly) return;
    const card = t.closest<HTMLElement>("[data-cr]")!;
    const r = find(card.dataset.cr!);
    if (!r) return;
    const c = choice(r.id);
    if (t.dataset.crPick) {
      c.picks[t.dataset.field!] = t.dataset.crPick as ChangeDecision;
      return repaint(card, r);
    }
    if (t.dataset.crAll !== undefined) {
      for (const f of r.fields) c.picks[f.field] = "angenommen";
      return repaint(card, r);
    }
    if (r.fields.some((f) => !c.picks[f.field])) return toast("Bitte jedes Feld übernehmen oder ablehnen.");
    const accepted = r.fields.filter((f) => c.picks[f.field] === "angenommen").length;
    void write(
      () => S.api.decideChangeRequest(r.id, { version: r.version, recordVersion: r.record?.version ?? null, ergebnis: c.picks, kommentar: c.comment.trim() || null }),
      accepted ? `Entschieden: ${accepted} von ${r.fields.length} übernommen` : "Entschieden: abgelehnt",
    ).then((ok) => {
      // Done, or someone was faster: the choices go with the request.
      if (ok) choices.delete(r.id);
    });
  });
  document.addEventListener("input", (e) => {
    const t = e.target as HTMLTextAreaElement;
    if (!t.matches("[data-cr] [data-cr-comment]")) return;
    choice(t.closest<HTMLElement>("[data-cr]")!.dataset.cr!).comment = t.value;
  });
}
